import { ReferralCode } from "@appally/collections/ReferralCode";
import { User, UserWithAssessment } from "@appally/collections/User";
import { collections } from "@appally/lib/data/firebase";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { assessmentsWithAdditionalFields } from "../actions/assessment";
import {
  getCurrentUser,
  getMyHelperOrInviter,
  isReferralCodeValid,
} from "../actions/user";

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: "user_1",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["User", "SupportedUsers", "Helper"],
  endpoints: (builder) => ({
    // Update the current logged in user
    createMe: builder.mutation<
      Pick<
        User,
        | "id"
        | "email"
        | "firstName"
        | "lastName"
        | "team_invite"
        | "agreed_terms"
        | "agreed_privacy"
        | "acknowledged_sensitive_questions"
        | "special_category_consent"
        | "referral_code_id"
        | "organisation_id"
        | "is_org_admin"
      >,
      Pick<
        User,
        | "id"
        | "email"
        | "firstName"
        | "lastName"
        | "team_invite"
        | "agreed_terms"
        | "agreed_privacy"
        | "acknowledged_sensitive_questions"
        | "special_category_consent"
        | "referral_code_id"
        | "is_org_admin"
      >
    >({
      invalidatesTags: ["User", "Helper"],
      queryFn: async (input) => {
        const user = {
          ...input,
          email: input.email?.trim(),
          special_category_consent: input.special_category_consent ?? false,
        };

        // Validate the referral code
        if (user.referral_code_id !== undefined) {
          await isReferralCodeValid(user.referral_code_id);
        } else {
          delete user.referral_code_id;
        }

        try {
          // Create the current user
          await setDoc(
            doc(collections.users, input.id),
            {
              ...user,
              created_at: Date.now(),
            },
            {
              merge: true,
            },
          );

          // Create the user's assessment doc
          if (!user.is_org_admin) {
            await setDoc(doc(collections.assessments, input.id), {
              assessment_user: input.id,
              id: input.id,
              activity: [
                {
                  title: "Account created",
                  timestamp: Date.now(),
                },
              ],
              answers: [],
              regenerate_analysis: false,
              managers_invited: {},
              created_at: Date.now(),
            });
          }

          return { data: user };
        } catch (e) {
          console.error(e);
          throw new Error("Sorry, something went wrong");
        }
      },
    }),

    // Get the current logged in user
    getMe: builder.query<User | null, void>({
      providesTags: ["User"],
      queryFn: async () => {
        const currentUser = await getCurrentUser();
        return { data: currentUser };
      },
    }),

    // Get the current logged in user referral details for data confirmation
    getReferral: builder.query<
      {
        referralData: ReferralCode[];
        userPreferences: any;
        hasReferralCode: boolean;
      },
      string
    >({
      providesTags: ["User"],
      queryFn: async (userId) => {
        const userRef = doc(collections.users, userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          console.log("User document not found");
          return {
            data: {
              referralData: [],
              userPreferences: null,
              hasReferralCode: false,
            },
          };
        }

        const userData = userDoc.data();
        const referralCodeId = userData.referral_code_id;

        const userPreferences = {
          sharingPermission: userData.sharingPermission,
          requiresAssistance: userData.requiresAssistance,
        };

        if (!referralCodeId) {
          console.log("No referral_code_id found");
          return {
            data: { referralData: [], userPreferences, hasReferralCode: false },
          };
        }

        // Rest of your existing logic
        const referralDoc = await getDocs(
          query(collections.referral_codes, where("id", "==", referralCodeId)),
        );

        if (referralDoc.empty)
          return {
            data: { referralData: [], userPreferences, hasReferralCode: true },
          }; // Fixed this too

        let referralData: any[] = [];

        for (const doc of referralDoc.docs) {
          try {
            const docData = doc.data(); // Changed variable name
            referralData.push(docData); // Use referralData array
          } catch (error) {
            throw new Error("Could not get referral data");
          }
        }
        return {
          data: { referralData, userPreferences, hasReferralCode: true },
        };
      },
    }),

    updatePreferences: builder.mutation<
      User,
      Pick<User, "sharingPermission" | "requiresAssistance">
    >({
      invalidatesTags: ["User"],
      queryFn: async ({ sharingPermission, requiresAssistance }) => {
        const currentUser = await getCurrentUser();

        if (!currentUser) throw new Error("You are not logged in");

        try {
          const newUser = {
            ...currentUser,
            last_saved: Date.now(),
          };
          if (sharingPermission !== undefined)
            newUser.sharingPermission = sharingPermission;
          if (requiresAssistance !== undefined)
            newUser.requiresAssistance = requiresAssistance;

          const docRef = doc(collections.users, currentUser.id);
          await setDoc(docRef, newUser);
          return { data: newUser };
        } catch (e) {
          throw new Error("Could not save the user");
        }
      },
    }),

    // Update the current logged in user
    updateMe: builder.mutation<
      User,
      Partial<Pick<User, "firstName" | "lastName" | "special_category_consent">>
    >({
      invalidatesTags: ["User"],
      queryFn: async ({ firstName, lastName, special_category_consent }) => {
        const currentUser = await getCurrentUser();
        if (!currentUser) throw new Error("You are not logged in");
        try {
          const newUser = {
            ...currentUser,
            last_saved: Date.now(),
          };
          if (firstName !== undefined) newUser.firstName = firstName;
          if (lastName !== undefined) newUser.lastName = lastName;
          if (special_category_consent !== undefined) {
            newUser.special_category_consent = special_category_consent;
          }
          const docRef = doc(collections.users, currentUser.id);
          await setDoc(docRef, newUser);
          return { data: newUser };
        } catch {
          throw new Error("Could not save the user");
        }
      },
    }),

    connectOrganisation: builder.mutation<
      User,
      Pick<User, "organisation_unique_code">
    >({
      invalidatesTags: ["User"],
      queryFn: async ({ organisation_unique_code }) => {
        const currentUser = await getCurrentUser();
        if (!currentUser) throw new Error("You are not logged in");

        const trimmedCode = organisation_unique_code?.trim();
        if (!trimmedCode) throw new Error("Enter your organisation code");

        const newUser = {
          ...currentUser,
          organisation_unique_code: trimmedCode,
          last_saved: Date.now(),
        };

        try {
          await setDoc(
            doc(collections.users, currentUser.id),
            {
              organisation_unique_code: trimmedCode,
              last_saved: Date.now(),
            },
            { merge: true },
          );

          return { data: newUser };
        } catch (e) {
          throw new Error("Could not connect your organisation");
        }
      },
    }),

    // Get the users that the current user supports
    getSupportedUsers: builder.query<UserWithAssessment[], void>({
      providesTags: ["SupportedUsers"],
      queryFn: async () => {
        // Get the current user
        const currentUser = await getCurrentUser();
        if (!currentUser) return { data: [] };

        // Get the assessments that this user supports
        const assessments = await getDocs(
          query(
            collections.assessments,
            where("assigned_helper_user", "==", currentUser.id),
          ),
        );

        if (assessments.empty) return { data: [] };

        let data: UserWithAssessment[] = [];
        for (const assessment of assessments.docs) {
          try {
            const userRef = doc(
              collections.users,
              assessment.data().assessment_user,
            );
            const user = await getDoc(userRef);
            if (!user.exists()) continue;

            const userData: UserWithAssessment = {
              ...user.data(),
              assessment: assessmentsWithAdditionalFields(assessment.data()),
            };

            data.push(userData);
          } catch (error) {
            throw new Error("Could not get user data");
          }
        }

        return { data };
      },
    }),

    // Get the helper for the current user
    getMyHelper: builder.query<User | undefined, void>({
      providesTags: ["Helper"],
      queryFn: async () => {
        const data = await getMyHelperOrInviter();
        return { data };
      },
    }),

    // Validate the referral code
    validateReferralCode: builder.mutation<boolean, string>({
      invalidatesTags: ["User", "Helper"],
      queryFn: async (referral_code_id) => {
        const data = await isReferralCodeValid(referral_code_id);
        return { data };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMeQuery,
  useUpdateMeMutation,
  useConnectOrganisationMutation,
  useGetSupportedUsersQuery,
  useGetMyHelperQuery,
  useCreateMeMutation,
  useValidateReferralCodeMutation,
  useGetReferralQuery,
  useUpdatePreferencesMutation,
} = userApi;
